import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Modal,
  ProgressBar,
  Row,
  Col,
  Card,
  Spinner,
} from "@themesberg/react-bootstrap";
import {
  calculateReward,
  calculateRewardUnmarketable,
} from "../../../../../controller/api/__classic__/result";
import { useCookies } from "react-cookie";

export default (props) => {
  const [totalTicket, setTotalTicket] = useState(0);
  const [done, setDone] = useState(false);
  const [totalUnmarketableTicket, setTotalUnmarketableTicket] = useState(0);
  const [doneUnmarketable, setDoneUnmarketable] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const prevShow = useRef(props.show);
  const totalRewardTicket = useRef(0);
  const totalUnrewardTicket = useRef(0);
  const totalReward = useRef(0);
  const totalRewardUnmarketableTicket = useRef(0);
  const totalUnrewardUnmarketableTicket = useRef(0);
  const totalRewardUnmarketable = useRef(0);
  const isCalculating = useRef(false);
  const isCalculatingUnmarketable = useRef(false);

  const [cookies] = useCookies(["lottmekong"]);

  const markFatalError = useCallback((message) => {
    setErrorMessage(message || "Không thể tính thưởng. Vui lòng thử lại.");
    setTotalTicket(0);
    setTotalUnmarketableTicket(0);
    setDone(true);
    setDoneUnmarketable(true);
    setIsStarting(false);
  }, []);

  const _onCalculating = useCallback(async () => {
    // Prevent duplicate API calls
    if (isCalculating.current) {
      return;
    }
    isCalculating.current = true;

    try {
      const response = await calculateReward(
        cookies.lottmekong,
        50,
        props.resultId,
      ); // Increased batch size

      if (!response) return;

      const totalTicketValue = Number(response.totalTicket);

      if (!Number.isFinite(totalTicketValue)) {
        markFatalError(response.message);
        return;
      }

      totalRewardTicket.current += Number(response.totalRewardTicket) || 0;
      totalUnrewardTicket.current += Number(response.totalUnrewardTicket) || 0;
      totalReward.current += Number(response.totalReward) || 0;

      setIsStarting(false);
      setTotalTicket(totalTicketValue);
      if (totalTicketValue === 0) {
        setDone(true);
      }
    } catch (err) {
      markFatalError("Tính thưởng vé bán thất bại");
    } finally {
      isCalculating.current = false;
    }
  }, [cookies.lottmekong, markFatalError, props.resultId]);

  const _onCalculatingUnmarketable = useCallback(async () => {
    if (isCalculatingUnmarketable.current) {
      return;
    }
    isCalculatingUnmarketable.current = true;

    try {
      const response = await calculateRewardUnmarketable(
        cookies.lottmekong,
        50,
        props.resultId,
      );

      if (!response) return;

      const totalTicketValue = Number(response.totalTicket);

      if (!Number.isFinite(totalTicketValue)) {
        markFatalError(response.message);
        return;
      }

      totalRewardUnmarketableTicket.current +=
        Number(response.totalRewardTicket) || 0;
      totalUnrewardUnmarketableTicket.current +=
        Number(response.totalUnrewardTicket) || 0;
      totalRewardUnmarketable.current += Number(response.totalReward) || 0;

      setIsStarting(false);
      setTotalUnmarketableTicket(totalTicketValue);
      if (totalTicketValue === 0) {
        setDoneUnmarketable(true);
      }
    } catch (err) {
      markFatalError("Tính thưởng vé ế thất bại");
    } finally {
      isCalculatingUnmarketable.current = false;
    }
  }, [cookies.lottmekong, markFatalError, props.resultId]);

  // Reset all counters when popup opens
  useEffect(() => {
    if (props.show && !prevShow.current) {
      setHasOpened(true);
      setIsStarting(true);
      setErrorMessage("");
      totalRewardTicket.current = 0;
      totalUnrewardTicket.current = 0;
      totalReward.current = 0;
      totalRewardUnmarketableTicket.current = 0;
      totalUnrewardUnmarketableTicket.current = 0;
      totalRewardUnmarketable.current = 0;
      isCalculating.current = false;
      isCalculatingUnmarketable.current = false;
      setTotalTicket(0);
      setDone(false);
      setTotalUnmarketableTicket(0);
      setDoneUnmarketable(false);

      // Bắt đầu với vé bán trước. Vé ế sẽ chạy sau khi vé bán xong
      // vì backend chỉ tạo record classicUnmarketableTicket ở batch cuối
      // của calculateReward (khi không còn vé bán nào status=3).
      _onCalculating();
    }
    prevShow.current = props.show;
  }, [props.show, _onCalculating]);

  // Continue calculating when there are more tickets
  useEffect(() => {
    if (props.show && totalTicket > 0 && !done) {
      _onCalculating();
    }
  }, [props.show, totalTicket, done, _onCalculating]);

  // Chỉ bắt đầu tính vé ế SAU KHI vé bán đã xong, vì backend tạo record
  // classicUnmarketableTicket ở nhánh kết thúc của calculateReward.
  // Nếu chạy song song, lần đầu mở popup bảng vé ế còn rỗng -> báo done ngay
  // -> user phải bấm lại lần 2 mới thấy tính thưởng vé ế.
  useEffect(() => {
    if (props.show && done && !doneUnmarketable && totalUnmarketableTicket === 0) {
      _onCalculatingUnmarketable();
    }
  }, [
    props.show,
    done,
    doneUnmarketable,
    totalUnmarketableTicket,
    _onCalculatingUnmarketable,
  ]);

  useEffect(() => {
    if (props.show && totalUnmarketableTicket > 0 && !doneUnmarketable) {
      _onCalculatingUnmarketable();
    }
  }, [
    props.show,
    totalUnmarketableTicket,
    doneUnmarketable,
    _onCalculatingUnmarketable,
  ]);

  const canClose = (done && doneUnmarketable) || Boolean(errorMessage);

  const _onHide = () => {
    if (canClose) {
      props.onHide(
        totalRewardTicket.current,
        totalUnrewardTicket.current,
        async () => {
          setHasOpened(false);
          setIsStarting(false);
          setErrorMessage("");
          totalRewardTicket.current = 0;
          totalUnrewardTicket.current = 0;
          totalReward.current = 0;
          totalRewardUnmarketableTicket.current = 0;
          totalUnrewardUnmarketableTicket.current = 0;
          totalRewardUnmarketable.current = 0;
          isCalculating.current = false;
          isCalculatingUnmarketable.current = false;
          setTotalTicket(0);
          setDone(false);
          setTotalUnmarketableTicket(0);
          setDoneUnmarketable(false);
          // No reload
        },
      );
    } else {
      alert("Chưa tính thưởng xong");
    }
  };

  const currentProcessed =
    totalRewardTicket.current + totalUnrewardTicket.current;
  const totalToProcess = currentProcessed + totalTicket;
  const progress =
    totalToProcess > 0 ? (currentProcessed / totalToProcess) * 100 : 0;

  const currentProcessedUnmarketable =
    totalRewardUnmarketableTicket.current +
    totalUnrewardUnmarketableTicket.current;
  const totalToProcessUnmarketable =
    currentProcessedUnmarketable + totalUnmarketableTicket;
  const progressUnmarketable =
    totalToProcessUnmarketable > 0
      ? (currentProcessedUnmarketable / totalToProcessUnmarketable) * 100
      : 0;

  return (
    <Modal
      {...props}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      backdrop="static"
      onHide={canClose ? _onHide : undefined} // Only allow closing via backdrop/esc if done (or just disable it)
    >
      <Modal.Header>
        <Modal.Title id="contained-modal-title-vcenter">
          Tính thưởng kỳ #{props.period && props.period.name}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4">
        {!hasOpened && <div className="text-center">Đang khởi tạo...</div>}

        {hasOpened && isStarting && (
          <div className="text-center text-muted mb-3">
            <Spinner animation="border" size="sm" className="me-2" />
            Đang bắt đầu tính thưởng...
          </div>
        )}

        {hasOpened && errorMessage && (
          <div className="text-center text-danger mb-3">{errorMessage}</div>
        )}

        {hasOpened &&
          (totalTicket > 0 || done || totalToProcess > 0) && (
            <>
              {(done || doneUnmarketable) && (
                <div className="text-center text-success mb-4">
                  <h4>Hoàn tất tính thưởng!</h4>
                </div>
              )}

              <div className="mb-4">
                <div className="d-flex justify-content-between mb-2">
                  <span>Tiến độ vé bán:</span>
                  <strong>
                    {Math.round(progress)}% ({currentProcessed}/{totalToProcess}
                    )
                  </strong>
                </div>
                <ProgressBar
                  animated={!done}
                  striped={!done}
                  variant={done ? "success" : "primary"}
                  now={progress}
                  style={{
                    height: "22px",
                    fontSize: "13px",
                    fontWeight: "bold",
                  }}
                  label={`${Math.round(progress)}%`}
                />
              </div>

              <Row className="mb-4">
                <Col md={4}>
                  <Card className="text-center border-success mb-3 shadow-sm">
                    <Card.Body className="p-3">
                      <h6 className="text-success mb-2">Vé bán trúng</h6>
                      <h3 className="mb-0">{totalRewardTicket.current}</h3>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="text-center border-danger mb-3 shadow-sm">
                    <Card.Body className="p-3">
                      <h6 className="text-danger mb-2">Vé bán trượt</h6>
                      <h3 className="mb-0">{totalUnrewardTicket.current}</h3>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="text-center border-warning mb-3 shadow-sm">
                    <Card.Body className="p-3">
                      <h6 className="text-warning mb-2">Thưởng vé bán</h6>
                      <h3
                        className="mb-0 text-warning"
                        style={{ fontSize: "1.5rem" }}
                      >
                        {new Intl.NumberFormat("vi-VN", {
                          style: "currency",
                          currency: "VND",
                        }).format(totalReward.current)}
                      </h3>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <div className="mb-4">
                <div className="d-flex justify-content-between mb-2">
                  <span>Tiến độ vé ế:</span>
                  <strong>
                    {Math.round(progressUnmarketable)}% (
                    {currentProcessedUnmarketable}/{totalToProcessUnmarketable})
                  </strong>
                </div>
                <ProgressBar
                  animated={!doneUnmarketable}
                  striped={!doneUnmarketable}
                  variant={doneUnmarketable ? "success" : "primary"}
                  now={progressUnmarketable}
                  style={{
                    height: "22px",
                    fontSize: "13px",
                    fontWeight: "bold",
                  }}
                  label={`${Math.round(progressUnmarketable)}%`}
                />
              </div>

              <Row>
                <Col md={4}>
                  <Card className="text-center border-success mb-3 shadow-sm">
                    <Card.Body className="p-3">
                      <h6 className="text-success mb-2">Vé ế trúng</h6>
                      <h3 className="mb-0">
                        {totalRewardUnmarketableTicket.current}
                      </h3>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="text-center border-danger mb-3 shadow-sm">
                    <Card.Body className="p-3">
                      <h6 className="text-danger mb-2">Vé ế trượt</h6>
                      <h3 className="mb-0">
                        {totalUnrewardUnmarketableTicket.current}
                      </h3>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="text-center border-warning mb-3 shadow-sm">
                    <Card.Body className="p-3">
                      <h6 className="text-warning mb-2">Thưởng vé ế</h6>
                      <h3
                        className="mb-0 text-warning"
                        style={{ fontSize: "1.5rem" }}
                      >
                        {new Intl.NumberFormat("vi-VN", {
                          style: "currency",
                          currency: "VND",
                        }).format(totalRewardUnmarketable.current)}
                      </h3>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          )}

        {hasOpened &&
          totalToProcess === 0 &&
          totalToProcessUnmarketable === 0 &&
          !done &&
          !doneUnmarketable && (
            <div className="text-center text-muted">
              Không có vé mới để tính thưởng.
            </div>
          )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant={canClose ? "success" : "secondary"}
          onClick={_onHide}
          disabled={!canClose}
        >
          {canClose ? "Hoàn thành" : "Đang xử lý..."}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
